import { Link } from 'react-router-dom';
import { Wrench } from 'lucide-react';
import { buttonClasses } from '@/components/Button';
import { useT } from '@/i18n/LocaleProvider';
import { registerEnglishArea } from '@/i18n/catalogue';
import { banners } from '@featit/shared/i18n/messages/en/banners';

registerEnglishArea('banners', banners);

/**
 * F-0160 AC-64 (I-0638): what the sign-in and sign-up screens say while the deployment has no
 * administrator.
 *
 * Blue, like the other "here is the next step" notices on these screens, not a refusal's amber:
 * nobody did anything wrong, the deployment is simply new. It names who acts — whoever runs the
 * deployment — and offers the one door that works, instead of a form that can only be refused.
 *
 * `prominent` where the notice replaces the form (sign-up), so its button is the page's primary
 * action; where it sits above a form that still works (sign-in), the form keeps that role.
 */
export function SetupRequiredNotice({ prominent = false }: { prominent?: boolean }) {
  const t = useT();
  return (
    <div
      role="status"
      data-testid="setup-required-notice"
      className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-[13px] text-blue-900 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-100"
    >
      <div className="flex items-start gap-2.5">
        <Wrench className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0">
          <span className="font-medium">{t('banners.setup.notSetUp')}</span>
          <span className="mt-0.5 block">{t('banners.setup.nobodyCanCreate')}</span>
        </span>
      </div>
      <Link
        to="/setup"
        className={buttonClasses({
          variant: prominent ? 'primary' : 'secondary',
          size: 'lg',
          className: 'mt-3 w-full',
        })}
      >
        {t('banners.setup.setUp')}
      </Link>
    </div>
  );
}
